import {
  RecsysClient,
  RecsysHttpError,
  RecsysIngestStatus,
  RecsysJobMatches,
  RecsysMatchRequest,
  RecsysMatchResponse,
  RecsysProfile,
  RecsysSearchRequest,
  RecsysSearchResponse,
  RecsysSimilarRequest,
  RecsysSimilarResponse,
} from './types';

/**
 * Typed REST client for the recsys BFF proxy (JWT-guarded, mounted at
 * `/recsys/*` — see packages/api/src/recsys-proxy/recsys-proxy.controller.ts).
 * These are plain REST routes (not GraphQL), so this mirrors the app's
 * existing raw-fetch pattern (packages/app/src/HTTP_Request/useHTTPGPTAXIOS.js)
 * rather than Apollo.
 *
 * KNOWN LIMITATION: unlike Apollo's error link (packages/app/src/apollo/index.ts),
 * this client does NOT silently refresh an expired access token — an expired
 * token surfaces as a 401 RecsysHttpError to the caller. Acceptable for the 2a
 * read-only browse use case: the recruiter re-navigates, or a concurrent
 * GraphQL call elsewhere on the page rotates the shared token via Apollo's
 * refresh flow. OPEN QUESTION for a later story: build a shared
 * fetch-with-refresh wrapper reusable by this client and useHTTPGPTAXIOS.
 */

const recsysBaseUrl = (): string =>
  (process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001') + '/recsys';

// Read fresh from localStorage on every call (not module init) so a token
// rotated by Apollo's refresh flow mid-session is always picked up.
const authHeaders = (): Record<string, string> => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${localStorage.getItem('token') || ''}`,
});

const parseBody = async (res: Response): Promise<unknown> => {
  try {
    return await res.json();
  } catch {
    return undefined;
  }
};

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${recsysBaseUrl()}${path}`, {
    ...init,
    headers: authHeaders(),
  });

  if (!res.ok) {
    const body = await parseBody(res);
    throw new RecsysHttpError(res.status, body);
  }

  return (await res.json()) as T;
}

export const recsysClient: RecsysClient = {
  match: (body: RecsysMatchRequest) =>
    request<RecsysMatchResponse>('/match', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  search: (body: RecsysSearchRequest) =>
    request<RecsysSearchResponse>('/search', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  // Collaborated Search (kan-82). Client paths omit the /v1 prefix — the proxy
  // maps /recsys/similar -> /v1/similar.
  similar: (body: RecsysSimilarRequest) =>
    request<RecsysSimilarResponse>('/similar', {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  candidateProfile: (id: string) =>
    request<RecsysProfile>(`/candidates/${encodeURIComponent(id)}/profile`),

  jobMatches: (jobId: string) =>
    request<RecsysJobMatches>(`/jobs/${encodeURIComponent(jobId)}/matches`),

  ingestStatus: () => request<RecsysIngestStatus>('/ingest/status'),
};

export default recsysClient;
